import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  CheckBox as SelectIcon
} from '@mui/icons-material';

/**
 * SelectionDialog - Modal para ações em lote
 * 
 * Exibe as ações de seleção em massa em um dialog/modal,
 * proporcionando melhor UX e não cobrindo os cards.
 */
const SelectionDialog = ({
  open,
  onClose,
  selectedCount,
  allSelected,
  onSelectAll,
  onEditFields,
  onNativeReview,
  onFavorite,
  onAddTag,
  onAddToCollection,
  onSetSentiment,
  onSetRead,
  onDelete,
  onClearSelection,
  onExportExcel,
  exportingExcel,
  showRemoveFromTag,
  onRemoveFromTag,
  showRemoveFromCollection,
  onRemoveFromCollection
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;

  const handleAction = (actionFn) => {
    return (...args) => {
      if (actionFn) {
        actionFn(...args);
      }
      // Não fecha automaticamente para permitir múltiplas ações
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          maxWidth: 900,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ py: 2, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SelectIcon color="primary" />
            <Typography variant="h6">
              Ações em Lote
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({selectedCount} {selectedCount === 1 ? 'item selecionado' : 'itens selecionados'})
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Escolha uma ação para aplicar aos <strong>{selectedCount}</strong> artigo{selectedCount !== 1 ? 's' : ''} selecionado{selectedCount !== 1 ? 's' : ''}:
          </Typography>
          
          {/* Não usar SelectionToolbar, criar layout próprio organizado */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Seção 1: Seleção */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                Seleção
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={onSelectAll}
                  sx={{ minWidth: 180 }}
                >
                  {allSelected ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={onClearSelection}
                  sx={{ minWidth: 180 }}
                >
                  Limpar Seleção
                </Button>
              </Box>
            </Box>

            <Divider />

            {/* Seção 2: Organização */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                Organização
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAction(onAddTag)}
                  sx={{ minWidth: 180 }}
                >
                  Adicionar Tags
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleAction(onAddToCollection)}
                  sx={{ minWidth: 180 }}
                >
                  Adicionar à Coleção
                </Button>
                {showRemoveFromTag && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleAction(onRemoveFromTag)}
                    sx={{ minWidth: 180 }}
                  >
                    Remover Tag
                  </Button>
                )}
                {onEditFields && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleAction(onEditFields)}
                    sx={{ minWidth: 180 }}
                  >
                    Editar campos
                  </Button>
                )}
                {onNativeReview && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleAction(onNativeReview)}
                    sx={{ minWidth: 180 }}
                  >
                    Revisao nativa
                  </Button>
                )}
                {showRemoveFromCollection && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleAction(onRemoveFromCollection)}
                    sx={{ minWidth: 180 }}
                  >
                    Remover da Coleção
                  </Button>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Seção 3: Status e Classificação */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                Status e Classificação
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleAction(onFavorite)}
                  sx={{ minWidth: 180 }}
                >
                  Favoritar
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  onClick={handleAction(onSetRead)}
                  sx={{ minWidth: 180 }}
                >
                  Marcar como Lido
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleAction(onSetSentiment)}
                  sx={{ minWidth: 180 }}
                >
                  Definir Sentimento
                </Button>
              </Box>
            </Box>

            <Divider />

            {/* Seção 4: Ações Especiais */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                Ações Especiais
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  sx={{
                    minWidth: 180,
                    bgcolor: 'success.main',
                    '&:hover': { bgcolor: 'success.dark' }
                  }}
                  onClick={handleAction(onExportExcel)}
                  disabled={exportingExcel}
                >
                  {exportingExcel ? 'Exportando...' : `Exportar (${selectedCount})`}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleAction(onDelete)}
                  sx={{ minWidth: 180 }}
                >
                  Excluir
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={onClearSelection}
          color="error"
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Limpar Seleção
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{
            minWidth: 80,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SelectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedCount: PropTypes.number.isRequired,
  allSelected: PropTypes.bool,
  onSelectAll: PropTypes.func,
  onEditFields: PropTypes.func,
  onNativeReview: PropTypes.func,
  onFavorite: PropTypes.func,
  onAddTag: PropTypes.func,
  onAddToCollection: PropTypes.func,
  onSetSentiment: PropTypes.func,
  onSetRead: PropTypes.func,
  onDelete: PropTypes.func,
  onClearSelection: PropTypes.func,
  onExportExcel: PropTypes.func,
  exportingExcel: PropTypes.bool,
  showRemoveFromTag: PropTypes.bool,
  onRemoveFromTag: PropTypes.func,
  showRemoveFromCollection: PropTypes.bool,
  onRemoveFromCollection: PropTypes.func
};

export default SelectionDialog;

